import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Badge from '@mui/material/Badge'
import Button from '@mui/material/Button'
import Portal from '@mui/material/Portal'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined'
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt'

import colors from '@/assets/scss/_themes-vars.module.scss'

import { useDiagnosticLayout } from '@/layouts/diagnostic/DiagnosticLayout'
import TopbarButton from '@/layouts/diagnostic/TopbarButton'

import DoctorIcon from '@/components/ui/icons/Doctor'
import PdsIcon from '@/components/ui/icons/Pds'

import type { Consultation } from '@/core/models/consultation.model'
import type { Staff } from '@/core/models/staff.model'

import { useAppDispatch } from '@/redux/rootReducer'
import { confirmReadingMessages } from '@/redux/reducers/chat-room.reducer'

import EndLiveConsultationDialog from '@/pages/diagnostic/containers/EndLiveConsultationDialog'
import InvitationDialog from '@/pages/diagnostic/containers/InvitationDialog'
import MeetingClockComponent from '@/pages/diagnostic/components/MeetingClock'
import Messenger from '@/pages/diagnostic/containers/Messenger'
import NetworkSpeed from '@/pages/diagnostic/containers/NetworkSpeed'

type TopbarProps = {
  authUser: Staff
  consultation: Consultation
  doctor: string
  pds: string
  isDoctorOnline: boolean
  isPdsOnline: boolean
  unreadChatMessages: any[]
  onCloseConsultation: () => void
}

export function Topbar({ authUser, consultation, doctor, pds, isDoctorOnline, isPdsOnline, unreadChatMessages, onCloseConsultation }: TopbarProps) {
  const { topbarContentContainer } = useDiagnosticLayout()

  const isConsultationActive = consultation.status === 'active'

  return (
    <Portal container={topbarContentContainer}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ flex: 1 }}>
        <ParticipantInfo
          authUser={authUser}
          consultation={consultation}
          doctor={doctor}
          pds={pds}
          isDoctorOnline={isDoctorOnline}
          isPdsOnline={isPdsOnline}
        />
        <Stack direction="row" alignItems="center" spacing={2}>
          <MeetingClockComponent startDate={consultation.startDate} consultationStatus={consultation.status} />
          <NetworkSpeed />
          <InviteButton consultation={consultation} />
          <MessengerButton consultation={consultation} unreadCount={unreadChatMessages.length} />
          <EndConsultationButton isActive={isConsultationActive} onCloseConsultation={onCloseConsultation} />
        </Stack>
      </Stack>
    </Portal>
  )
}

// Sub-components
function ParticipantInfo({ authUser, consultation, doctor, pds, isDoctorOnline, isPdsOnline }: Omit<TopbarProps, 'unreadChatMessages' | 'onCloseConsultation'>) {
  const { t } = useTranslation()
  const isDoctor = authUser._id === consultation.medecin?._id

  if (isDoctor) {
    return (
      <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
        <PdsIcon
          isFemale={consultation.pds?.userInfo.gender === 'F'}
          htmlColor="#FFF"
          height={'45px'}
          width={'45px'}
          sx={{
            backgroundColor: 'white',
            height: '45px',
            width: '45px',
            border: '3px solid',
            borderColor: isPdsOnline ? colors.green400 : colors.red500,
            borderRadius: '40px',
          }}
        />
        <Stack direction="column">
          <Typography variant="h1" sx={{ color: (theme) => theme.palette.common.white, fontSize: '14px', fontWeight: 500 }}>
            {pds}
          </Typography>
          <Typography fontSize="10px" fontWeight="300" sx={{ color: (theme) => theme.palette.common.white }}>
            {isPdsOnline ? t('common:online') : t('common:offline')}
          </Typography>
        </Stack>
      </Stack>
    )
  }

  return (
    <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
      <DoctorIcon
        isFemale={consultation.medecin?.userInfo.gender === 'F'}
        htmlColor="#FFF"
        height={'45px'}
        width={'45px'}
        sx={{
          backgroundColor: 'white',
          height: '45px',
          width: '45px',
          border: '3px solid',
          borderColor: isDoctorOnline ? colors.green400 : colors.red500,
          borderRadius: '40px',
        }}
      />
      <Stack direction="column">
        <Typography variant="h1" sx={{ color: (theme) => theme.palette.common.white, fontSize: '14px', fontWeight: 500 }}>
          Dr. {doctor}
        </Typography>
        <Typography fontSize="10px" fontWeight="300" sx={{ color: (theme) => theme.palette.common.white }}>
          {isDoctorOnline ? t('common:online') : t('common:offline')}
        </Typography>
      </Stack>
    </Stack>
  )
}

function InviteButton({ consultation }: { consultation: Consultation }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)

  const handleToggle = () => setOpen((prev) => !prev)

  return (
    <>
      <Tooltip title={t('layouts.diagnostic.header.openParticipantsButton.tooltip')}>
        <TopbarButton onClick={handleToggle} variant="contained">
          <PersonAddAltIcon />
        </TopbarButton>
      </Tooltip>
      <InvitationDialog open={open} consultation={consultation} onClose={handleToggle} />
    </>
  )
}

function MessengerButton({ consultation, unreadCount }: { consultation: Consultation; unreadCount: number }) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [open, setOpen] = useState(false)

  const handleToggle = () => setOpen((prev) => !prev)

  // Mark messages as read when messenger is open
  useEffect(() => {
    if (consultation.chatRoom && open && unreadCount) {
      dispatch(confirmReadingMessages(consultation.chatRoom))
    }
  }, [consultation.chatRoom, dispatch, open, unreadCount])

  return (
    <>
      <Badge badgeContent={unreadCount} color="error" max={9} overlap="rectangular">
        <Tooltip title={t('layouts.diagnostic.header.openMessengerButton.tooltip')}>
          <TopbarButton onClick={handleToggle} variant="contained">
            <ChatOutlinedIcon />
          </TopbarButton>
        </Tooltip>
      </Badge>
      <Messenger chatRoomId={consultation.chatRoom} onClose={handleToggle} open={open} />
    </>
  )
}

function EndConsultationButton({ isActive, onCloseConsultation }: { isActive: boolean; onCloseConsultation: () => void }) {
  const { t } = useTranslation()
  const [dialogOpen, setDialogOpen] = useState(false)

  const handleToggleDialog = () => setDialogOpen((prev) => !prev)

  const handleClick = () => {
    if (isActive) {
      handleToggleDialog()
    } else {
      onCloseConsultation()
    }
  }

  return (
    <>
      <Button
        color="error"
        onClick={handleClick}
        size="small"
        variant="contained"
        sx={{
          borderRadius: '15px',
          fontSize: '12px',
        }}
      >
        {t(`layouts.diagnostic.header.${isActive ? 'endConsultationButton' : 'quitConsultationButton'}.label`)}
      </Button>
      <EndLiveConsultationDialog onClose={handleToggleDialog} open={dialogOpen} />
    </>
  )
}
